import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { CustomersTab } from "../components/customers/CustomersTab";
import { LoyaltyMiniDashboard } from "../components/customers/LoyaltyMiniDashboard";
import LoyaltyScannerDialog from "../components/LoyaltyScannerDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  Plus,
  Loader2,
  Users,
  Star,
  Gift,
  TrendingUp,
  TrendingDown,
  Settings,
  QrCode,
  ShoppingBag,
  MoreVertical,
  Trash2,
  CreditCard,
  Copy,
  Check,
  ExternalLink,
  Award,
  Zap,
  RefreshCw,
  BarChart2,
  Pencil,
  ToggleLeft,
  ToggleRight,
  UserCheck,
  UserX,
  Ticket,
  Activity,
  Cake,
  Send,
  Upload,
  Megaphone,
  Sparkles,
  FileDown
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/layout/PageHeader";
import { PageSkeleton } from "../components/layout/PageSkeleton";
import { generateLoyaltyPosterPDF, generateLoyaltyTableTentPDF, generateLoyaltyStoryPNG } from "../lib/loyaltyPoster";
import { formatCurrency, formatDate } from "../lib/utils";
import LoyaltyCardPreview from "../components/customers/LoyaltyCardPreview";
import StatCard from "../components/customers/StatCard";
import LastCampaignCard from "../components/customers/LastCampaignCard";
import PreferencesStatsCard from "../components/customers/PreferencesStatsCard";
import CsvImportDialog from "../components/customers/CsvImportDialog";
import CampaignDialog from "../components/customers/CampaignDialog";
import { EMPTY_REWARD } from "../components/customers/constants";
export default function CustomersPage() {
  const { currentRestaurant } = useOutletContext();

  const [customers, setCustomers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);
  const [loyaltyStats, setLoyaltyStats] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [posterLoading, setPosterLoading] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [activeTab, setActiveTab] = useState("customers");

  // Dialogs
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null); // null = create, obj = edit
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [couponResult, setCouponResult] = useState(null);

  const [customerForm, setCustomerForm] = useState({ name: "", email: "", phone: "", notes: "", birthday: "" });
  const [rewardForm, setRewardForm] = useState(EMPTY_REWARD);
  const [purchaseAmount, setPurchaseAmount] = useState("");
  const [birthdayCustomers, setBirthdayCustomers] = useState([]);
  const [sendingBirthday, setSendingBirthday] = useState({});
  const [configForm, setConfigForm] = useState({
    program_name: "Club de Fidelización",
    points_per_euro: 1.0,
    points_per_visit: 0,
    primary_color: "#F97316",
    secondary_color: "#1F2937",
    levels: [
      { name: "Bronce", min_points: 0, color: "#CD7F32", benefits: "Bienvenido al club" },
      { name: "Plata", min_points: 200, color: "#C0C0C0", benefits: "5% de descuento especial" },
      { name: "Oro", min_points: 500, color: "#FFD700", benefits: "10% de descuento + prioridad" }
    ],
    birthday_discount_enabled: false,
    birthday_discount_type: "discount_percentage",
    birthday_discount_value: 10,
    birthday_message: "Feliz cumpleaños! Disfruta de tu descuento especial"
  });

  const fetchData = useCallback(async () => {
    if (!currentRestaurant) return;
    try {
      const [customersData, rewardsData, configData, birthdaysData] = await Promise.all([
        api.getCustomers(currentRestaurant.restaurant_id),
        api.getRewards(currentRestaurant.restaurant_id),
        api.getLoyaltyConfig(currentRestaurant.restaurant_id),
        api.getBirthdaysToday(currentRestaurant.restaurant_id).catch(() => [])
      ]);
      setCustomers(customersData);
      setRewards(rewardsData);
      setLoyaltyConfig(configData);
      setBirthdayCustomers(birthdaysData);
      if (configData) {
        setConfigForm({
          program_name: configData.program_name || "Club de Fidelización",
          points_per_euro: configData.points_per_euro || 1.0,
          points_per_visit: configData.points_per_visit || 0,
          primary_color: configData.primary_color || "#F97316",
          secondary_color: configData.secondary_color || "#1F2937",
          levels: configData.levels || [],
          birthday_discount_enabled: configData.birthday_discount_enabled || false,
          birthday_discount_type: configData.birthday_discount_type || "discount_percentage",
          birthday_discount_value: configData.birthday_discount_value ?? 10,
          birthday_message: configData.birthday_message || "Feliz cumpleaños! Disfruta de tu descuento especial"
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentRestaurant]);

  const fetchStats = useCallback(async () => {
    if (!currentRestaurant) return;
    setStatsLoading(true);
    try {
      const stats = await api.getLoyaltyStats(currentRestaurant.restaurant_id);
      setLoyaltyStats(stats);
    } catch (err) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setStatsLoading(false);
    }
  }, [currentRestaurant]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (activeTab === "stats") fetchStats();
  }, [activeTab, fetchStats]);

  // ── QR ──────────────────────────────────────────────────────────────────────
  const loadQR = async () => {
    setQrLoading(true);
    try {
      const data = await api.getLoyaltyQR(currentRestaurant.restaurant_id);
      setQrData(data);
    } catch { toast.error("Error al generar el QR"); }
    finally { setQrLoading(false); }
  };

  const handleDownloadAsset = async (kind) => {
    if (!qrData?.qr_code) return toast.error("Genera el QR primero");
    setPosterLoading(true);
    try {
      const cheapest = rewards
        .filter(r => r.is_active !== false && (r.points_required || 0) > 0)
        .reduce((m, r) => (m === null || r.points_required < m ? r.points_required : m), null);
      const args = {
        restaurantName: currentRestaurant?.name || "Tu restaurante",
        logoUrl: currentRestaurant?.logo_url || null,
        qrDataUrl: qrData.qr_code,
        portalUrl: qrData.portal_url,
        pointsPerEuro: loyaltyConfig?.points_per_euro || 1,
        firstRewardPoints: cheapest,
      };
      if (kind === "a4") {
        await generateLoyaltyPosterPDF(args);
        toast.success("Póster A4 listo. Imprime y déjalo a la vista 🎉");
      } else if (kind === "tent") {
        await generateLoyaltyTableTentPDF(args);
        toast.success("Tarjeta de mesa lista 🍽️");
      } else if (kind === "story") {
        await generateLoyaltyStoryPNG(args);
        toast.success("Imagen para Stories descargada 📱");
      }
    } catch (err) {
      toast.error("No se pudo generar el archivo");
      // eslint-disable-next-line no-console
      console.error("[Asset]", err);
    } finally {
      setPosterLoading(false);
    }
  };

  // ── Customers ───────────────────────────────────────────────────────────────
  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (!customerForm.name.trim()) return toast.error("El nombre es requerido");
    setSaving(true);
    try {
      const c = await api.createCustomer(currentRestaurant.restaurant_id, customerForm);
      setCustomers([...customers, c]);
      setCustomerDialogOpen(false);
      setCustomerForm({ name: "", email: "", phone: "", notes: "", birthday: "" });
      toast.success("Cliente añadido");
    } catch (err) { toast.error(err.message || "Error al crear cliente"); }
    finally { setSaving(false); }
  };

  const handleDeleteCustomer = async (customerId) => {
    try {
      await api.deleteCustomer(customerId);
      setCustomers(customers.filter(c => c.customer_id !== customerId));
      toast.success("Cliente eliminado");
    } catch { toast.error("Error al eliminar cliente"); }
  };

  // ── Purchase ─────────────────────────────────────────────────────────────────
  const handleAddPurchase = async (e) => {
    e.preventDefault();
    if (!purchaseAmount || isNaN(parseFloat(purchaseAmount))) return toast.error("Introduce un importe válido");
    setSaving(true);
    try {
      const result = await api.addPurchase(selectedCustomer.customer_id, parseFloat(purchaseAmount));
      toast.success(result.message);
      setPurchaseDialogOpen(false);
      setPurchaseAmount("");
      fetchData();
    } catch (err) { toast.error(err.message || "Error al añadir puntos"); }
    finally { setSaving(false); }
  };

  // ── Redeem ───────────────────────────────────────────────────────────────────
  const handleRedeemReward = async (rewardId) => {
    setSaving(true);
    try {
      const result = await api.redeemReward(selectedCustomer.customer_id, rewardId);
      setCouponResult(result);
      fetchData();
    } catch (err) { toast.error(err.message || "Puntos insuficientes"); }
    finally { setSaving(false); }
  };

  // ── Rewards CRUD ─────────────────────────────────────────────────────────────
  const openCreateReward = () => { setEditingReward(null); setRewardForm(EMPTY_REWARD); setRewardDialogOpen(true); };
  const openEditReward = (r) => {
    setEditingReward(r);
    setRewardForm({
      name: r.name,
      description: r.description || "",
      points_required: String(r.points_required),
      reward_type: r.reward_type,
      reward_value: String(r.reward_value),
      is_active: r.is_active
    });
    setRewardDialogOpen(true);
  };

  const handleSaveReward = async (e) => {
    e.preventDefault();
    if (!rewardForm.name.trim() || !rewardForm.points_required || !rewardForm.reward_value)
      return toast.error("Nombre, puntos y valor son requeridos");
    setSaving(true);
    try {
      const payload = {
        ...rewardForm,
        points_required: parseInt(rewardForm.points_required),
        reward_value: parseFloat(rewardForm.reward_value)
      };
      if (editingReward) {
        const updated = await api.updateReward(editingReward.reward_id, payload);
        setRewards(rewards.map(r => r.reward_id === editingReward.reward_id ? updated : r));
        toast.success("Recompensa actualizada");
      } else {
        const created = await api.createReward(currentRestaurant.restaurant_id, payload);
        setRewards([...rewards, created]);
        toast.success("Recompensa creada");
      }
      setRewardDialogOpen(false);
    } catch (err) { toast.error(err.message || "Error al guardar recompensa"); }
    finally { setSaving(false); }
  };

  const handleDeleteReward = async (rewardId) => {
    try {
      await api.deleteReward(rewardId);
      setRewards(rewards.filter(r => r.reward_id !== rewardId));
      toast.success("Recompensa eliminada");
    } catch { toast.error("Error al eliminar recompensa"); }
  };

  const handleToggleReward = async (rewardId) => {
    try {
      const result = await api.toggleReward(rewardId);
      setRewards(rewards.map(r => r.reward_id === rewardId ? { ...r, is_active: result.is_active } : r));
      toast.success(result.is_active ? "Recompensa activada" : "Recompensa desactivada");
    } catch { toast.error("Error al cambiar estado"); }
  };

  // ── Config ───────────────────────────────────────────────────────────────────
  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await api.saveLoyaltyConfig(currentRestaurant.restaurant_id, configForm);
      setLoyaltyConfig(saved);
      toast.success("Configuración guardada");
    } catch (err) { toast.error(err.message || "Error al guardar configuración"); }
    finally { setSaving(false); }
  };

  const handleSendBirthdayDiscount = async (customerId) => {
    setSendingBirthday(prev => ({ ...prev, [customerId]: true }));
    try {
      const result = await api.sendBirthdayDiscount(currentRestaurant.restaurant_id, customerId);
      if (result.already_sent) {
        toast.info(`Ya se envió el descuento hoy: ${result.coupon_code}`);
      } else {
        toast.success(`Descuento enviado a ${result.customer_name}: ${result.coupon_code}`);
      }
    } catch (err) { toast.error(err.message || "Error al enviar descuento"); }
    finally { setSendingBirthday(prev => ({ ...prev, [customerId]: false })); }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getCustomerLevel = (pts) => {
    if (!loyaltyConfig?.levels?.length) return null;
    return [...loyaltyConfig.levels].sort((a, b) => b.min_points - a.min_points).find(l => pts >= l.min_points) || null;
  };
  const getNextLevel = (pts) => {
    if (!loyaltyConfig?.levels?.length) return null;
    return [...loyaltyConfig.levels].sort((a, b) => a.min_points - b.min_points).find(l => l.min_points > pts) || null;
  };
  const copyUrl = () => {
    if (qrData?.portal_url) { navigator.clipboard.writeText(qrData.portal_url); setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2000); }
  };
  const rewardLabel = (type, value) => {
    if (type === "discount_percentage") return `${value}% descuento`;
    if (type === "discount_amount") return `${value}€ descuento`;
    return "Artículo gratis";
  };

  const formatBirthday = (mmdd) => {
    if (!mmdd) return "—";
    const [mm, dd] = mmdd.split("-");
    const months = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
    return `${parseInt(dd)} ${months[parseInt(mm) - 1] || mm}`;
  };
  const todayMmdd = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" }).replace("/", "-");
  const isBirthdayToday = (c) => c.birthday === todayMmdd;

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) return <PageSkeleton kpis={4} showTabs rows={5} />;

  // ── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <PageHeader
        icon={<Users className="w-5 h-5" />}
        title="Fidelización"
        description="Gestiona clientes, programa de puntos y recompensas."
        breadcrumb={[{ label: "Ventas y caja" }, { label: "Fidelización" }]}
        actions={
          <>
            <Button variant="outline" className="rounded-full" onClick={() => setScannerOpen(true)} data-testid="open-scanner-btn">
              <QrCode className="w-4 h-4 mr-2" /> Escanear cliente
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => setCsvImportOpen(true)} data-testid="open-csv-import-btn">
              <Upload className="w-4 h-4 mr-2" /> Importar CSV
            </Button>
            {customers.length > 0 && (
              <Button variant="outline" className="rounded-full" onClick={() => setCampaignOpen(true)} data-testid="open-campaign-btn">
                <Megaphone className="w-4 h-4 mr-2" /> Enviar campaña
              </Button>
            )}
            <Button className="rounded-full" onClick={() => setCustomerDialogOpen(true)} data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo cliente
            </Button>
          </>
        }
      />

      {/* Loyalty mini-dashboard (KPIs + birthdays + top customers) */}
      {currentRestaurant?.restaurant_id && (
        <LoyaltyMiniDashboard restaurantId={currentRestaurant.restaurant_id} />
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="customers" data-testid="tab-customers">
            <Users className="w-4 h-4 mr-2" /> Clientes
          </TabsTrigger>
          <TabsTrigger value="program" data-testid="tab-program">
            <Settings className="w-4 h-4 mr-2" /> Programa
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">
            <BarChart2 className="w-4 h-4 mr-2" /> Estadísticas
          </TabsTrigger>
          <TabsTrigger value="qr" data-testid="tab-qr">
            <QrCode className="w-4 h-4 mr-2" /> QR Registro
          </TabsTrigger>
        </TabsList>

        {/* ─── CLIENTES ─────────────────────────────────────────────────────── */}
        <TabsContent value="customers">
          <CustomersTab
            customers={customers}
            birthdayCustomers={birthdayCustomers}
            configForm={configForm}
            sendingBirthday={sendingBirthday}
            levels={configForm.levels || []}
            onAddCustomer={() => setCustomerDialogOpen(true)}
            onSendBirthday={handleSendBirthdayDiscount}
            onRegisterPurchase={(c) => { setSelectedCustomer(c); setPurchaseDialogOpen(true); }}
            onRedeemReward={(c) => { setSelectedCustomer(c); setCouponResult(null); setRedeemDialogOpen(true); }}
            onDeleteCustomer={handleDeleteCustomer}
            onPointsAdjusted={() => { fetchData(); }}
            getCustomerLevel={getCustomerLevel}
            getNextLevel={getNextLevel}
          />
        </TabsContent>

        {/* ─── PROGRAMA ─────────────────────────────────────────────────────── */}
        <TabsContent value="program">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Config Form */}
            <Card className="border-none shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2"><Zap className="w-5 h-5 text-primary" /> Configuración</CardTitle>
                <CardDescription>Define las reglas del programa de puntos</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveConfig} className="space-y-5">
                  <div className="space-y-2">
                    <Label>Nombre del programa</Label>
                    <Input value={configForm.program_name} onChange={e => setConfigForm({ ...configForm, program_name: e.target.value })} data-testid="program-name-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Puntos por euro</Label>
                      <Input type="number" step="0.5" min="0.1" value={configForm.points_per_euro}
                        onChange={e => setConfigForm({ ...configForm, points_per_euro: parseFloat(e.target.value) || 1 })}
                        data-testid="points-per-euro-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Puntos por visita</Label>
                      <Input type="number" min="0" value={configForm.points_per_visit}
                        onChange={e => setConfigForm({ ...configForm, points_per_visit: parseInt(e.target.value) || 0 })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {[["Color principal", "primary_color"], ["Color secundario", "secondary_color"]].map(([lbl, key]) => (
                      <div key={key} className="space-y-2">
                        <Label>{lbl}</Label>
                        <div className="flex gap-2">
                          <input type="color" value={configForm[key]}
                            onChange={e => setConfigForm({ ...configForm, [key]: e.target.value })}
                            className="w-10 h-10 rounded border cursor-pointer" />
                          <Input value={configForm[key]} onChange={e => setConfigForm({ ...configForm, [key]: e.target.value })} />
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Levels */}
                  <div className="space-y-3">
                    <Label>Niveles de fidelización</Label>
                    {configForm.levels.map((level, idx) => (
                      <div key={level.id || level.level_id || `level-${idx}`} className="grid grid-cols-3 gap-2 items-center p-3 bg-muted/30 rounded-lg">
                        <Input placeholder="Nombre" value={level.name}
                          onChange={e => { const nl = [...configForm.levels]; nl[idx] = { ...nl[idx], name: e.target.value }; setConfigForm({ ...configForm, levels: nl }); }} />
                        <Input type="number" placeholder="Puntos mín." value={level.min_points}
                          onChange={e => { const nl = [...configForm.levels]; nl[idx] = { ...nl[idx], min_points: parseInt(e.target.value) || 0 }; setConfigForm({ ...configForm, levels: nl }); }} />
                        <div className="flex gap-2 items-center">
                          <input type="color" value={level.color}
                            onChange={e => { const nl = [...configForm.levels]; nl[idx] = { ...nl[idx], color: e.target.value }; setConfigForm({ ...configForm, levels: nl }); }}
                            className="w-8 h-8 rounded border cursor-pointer" />
                          <Button type="button" variant="ghost" size="icon" className="text-destructive"
                            onClick={() => setConfigForm({ ...configForm, levels: configForm.levels.filter((_, i) => i !== idx) })}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => setConfigForm({ ...configForm, levels: [...configForm.levels, { name: "", min_points: 0, color: "#888888", benefits: "" }] })}>
                      <Plus className="w-3 h-3 mr-1" /> Añadir nivel
                    </Button>
                  </div>
                  {/* Birthday discount config */}
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm font-semibold flex items-center gap-2"><Cake className="w-4 h-4 text-amber-500" /> Descuento de cumpleaños</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Envía descuentos automáticos a clientes en su cumpleaños</p>
                      </div>
                      <Switch
                        checked={configForm.birthday_discount_enabled}
                        onCheckedChange={v => setConfigForm({ ...configForm, birthday_discount_enabled: v })}
                        data-testid="birthday-discount-toggle"
                      />
                    </div>
                    {configForm.birthday_discount_enabled && (
                      <div className="space-y-3 pl-1">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Tipo de descuento</Label>
                            <Select value={configForm.birthday_discount_type} onValueChange={val => setConfigForm({ ...configForm, birthday_discount_type: val })}>
                              <SelectTrigger data-testid="birthday-discount-type">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="discount_percentage">% Descuento</SelectItem>
                                <SelectItem value="discount_amount">Descuento fijo (€)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>{configForm.birthday_discount_type === "discount_percentage" ? "Valor (%)" : "Valor (€)"}</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={configForm.birthday_discount_value}
                              onChange={e => setConfigForm({ ...configForm, birthday_discount_value: parseFloat(e.target.value) || 0 })}
                              data-testid="birthday-discount-value"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Mensaje personalizado</Label>
                          <Input
                            placeholder="Feliz cumpleaños! Disfruta de tu descuento especial"
                            value={configForm.birthday_message}
                            onChange={e => setConfigForm({ ...configForm, birthday_message: e.target.value })}
                            data-testid="birthday-message-input"
                          />
                        </div>
                        <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                          <p className="text-xs text-green-700 dark:text-green-400 font-medium flex items-center gap-1.5">
                            <Zap className="w-3.5 h-3.5" />
                            Envio automatico activado — los descuentos se envian cada dia a las 8:00 (UTC) a los clientes que cumplan anos ese dia.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <Button type="submit" disabled={saving} className="w-full" data-testid="save-config-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Guardar configuración
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Right: Card preview + Rewards */}
            <div className="space-y-6">
              <Card className="border-none shadow-soft">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" /> Vista previa</CardTitle>
                </CardHeader>
                <CardContent>
                  <LoyaltyCardPreview
                    customer={{ name: "Nombre del Cliente", points: 340, total_visits: 12, total_spent: 280 }}
                    config={configForm}
                    currentLevel={configForm.levels.filter(l => 340 >= l.min_points).sort((a, b) => b.min_points - a.min_points)[0]}
                    restaurantName={currentRestaurant?.name}
                  />
                </CardContent>
              </Card>

              {/* Rewards */}
              <Card className="border-none shadow-soft">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> Recompensas</CardTitle>
                    <Button size="sm" variant="outline" onClick={openCreateReward} data-testid="add-reward-btn">
                      <Plus className="w-4 h-4 mr-1" /> Nueva
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {rewards.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">Sin recompensas aún</p>
                  ) : (
                    <div className="space-y-2">
                      {rewards.map(r => (
                        <div key={r.reward_id}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-opacity ${r.is_active ? "bg-muted/30" : "opacity-50 bg-muted/10"}`}
                          data-testid={`reward-item-${r.reward_id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">{r.name}</p>
                              {!r.is_active && <Badge variant="outline" className="text-xs shrink-0">Inactiva</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground">{r.points_required} pts · {rewardLabel(r.reward_type, r.reward_value)}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {/* Toggle active */}
                            <Switch
                              checked={r.is_active}
                              onCheckedChange={() => handleToggleReward(r.reward_id)}
                              data-testid={`toggle-reward-${r.reward_id}`}
                            />
                            {/* Edit */}
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditReward(r)} data-testid={`edit-reward-${r.reward_id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            {/* Delete */}
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteReward(r.reward_id)} data-testid={`delete-reward-${r.reward_id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ─── ESTADÍSTICAS ──────────────────────────────────────────────────── */}
        <TabsContent value="stats" data-testid="stats-tab-content">
          {currentRestaurant?.restaurant_id && (
            <div className="mb-6 space-y-6">
              <LastCampaignCard restaurantId={currentRestaurant.restaurant_id} />
              <PreferencesStatsCard restaurantId={currentRestaurant.restaurant_id} />
            </div>
          )}
          {statsLoading ? (
            <div className="flex items-center justify-center min-h-[300px]">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : !loyaltyStats ? (
            <div className="text-center py-16">
              <BarChart2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No hay datos de estadísticas aún</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Engagement summary — two compact rate pills (unique to this tab) */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-border/60 bg-card/80 p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                      <Activity className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Engagement</p>
                      <p className="text-xs text-muted-foreground/70">visitaron en últimos 30 días</p>
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-blue-600">{loyaltyStats.summary.engagement_rate}%</p>
                </div>
                <div className="rounded-2xl border border-border/60 bg-card/80 p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center">
                      <UserX className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Churn</p>
                      <p className="text-xs text-muted-foreground/70">+90 días sin visita</p>
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-red-500">{loyaltyStats.summary.churn_rate}%</p>
                </div>
              </div>

              {/* Averages — finer-grained detail not present in mini-dashboard */}
              <div className="grid grid-cols-3 gap-4">
                <StatCard label="Puntos promedio / cliente" value={loyaltyStats.summary.avg_points_per_customer.toLocaleString()} icon={Star} />
                <StatCard label="Visitas promedio / cliente" value={loyaltyStats.summary.avg_visits_per_customer} icon={TrendingUp} />
                <StatCard label="Gasto promedio / cliente" value={formatCurrency(loyaltyStats.summary.avg_spent_per_customer)} icon={CreditCard} />
              </div>

              {/* Charts row */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Monthly new customers */}
                <Card className="border-none shadow-soft">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" /> Nuevos clientes por mes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={loyaltyStats.monthly_new_customers} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                        <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{ borderRadius: "8px", fontSize: "12px" }}
                          formatter={(v) => [v, "Nuevos clientes"]}
                        />
                        <Bar dataKey="customers" fill="var(--color-primary, #F97316)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Level distribution */}
                <Card className="border-none shadow-soft">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base flex items-center gap-2">
                      <Award className="w-4 h-4 text-primary" /> Distribución por nivel
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loyaltyStats.level_distribution.length === 0 ? (
                      <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">Sin datos de nivel</div>
                    ) : (
                      <div className="flex flex-col gap-4">
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie
                              data={loyaltyStats.level_distribution}
                              dataKey="count"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={75}
                              innerRadius={40}
                              paddingAngle={3}
                            >
                              {loyaltyStats.level_distribution.map((entry, i) => (
                                <Cell key={entry.name || `cell-${i}`} fill={entry.color || "#888"} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ borderRadius: "8px", fontSize: "12px" }} formatter={(v, n) => [v, n]} />
                            <Legend wrapperStyle={{ fontSize: "12px" }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="grid grid-cols-3 gap-2">
                          {loyaltyStats.level_distribution.map((l) => (
                            <div key={l.name} className="text-center p-2 rounded-lg" style={{ background: (l.color || "#888") + "20" }}>
                              <p className="text-lg font-bold" style={{ color: l.color || "#888" }}>{l.count}</p>
                              <p className="text-xs text-muted-foreground">{l.name}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Recent redemptions */}
              <Card className="border-none shadow-soft">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-primary" /> Últimas recompensas canjeadas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loyaltyStats.recent_redemptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No hay canjes aún</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Recompensa</TableHead>
                          <TableHead>Código cupón</TableHead>
                          <TableHead>Puntos usados</TableHead>
                          <TableHead>Fecha</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loyaltyStats.recent_redemptions.map((red) => (
                          <TableRow key={red.redemption_id} data-testid={`redemption-row-${red.redemption_id}`}>
                            <TableCell className="font-medium">{red.customer_name}</TableCell>
                            <TableCell>{red.reward_name}</TableCell>
                            <TableCell>
                              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{red.coupon_code}</code>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">{red.points_used} pts</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {red.created_at ? new Date(red.created_at).toLocaleDateString("es-ES") : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ─── QR ───────────────────────────────────────────────────────────── */}
        <TabsContent value="qr">
          {/* Promotional pitch card — what the customer gets when they scan */}
          <div className="rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 via-orange-50/40 to-amber-50/40 dark:from-primary/10 dark:via-orange-950/20 dark:to-amber-950/20 p-5 mb-6" data-testid="qr-pitch-card">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-base font-semibold">¿Qué consigue tu cliente al escanear?</h3>
                <p className="text-sm text-muted-foreground">Imprime el QR y dáselo al cliente diciendo esto:</p>
              </div>
            </div>
            <blockquote className="text-sm italic text-foreground/85 border-l-2 border-primary/40 pl-4 mb-4">
              {"\u201c"}Escanea este QR y únete a nuestro programa de fidelización. Por cada{" "}
              <strong>1€</strong> que gastes ganas{" "}
              <strong>{loyaltyConfig?.points_per_euro || 1} punto{(loyaltyConfig?.points_per_euro || 1) === 1 ? "" : "s"}</strong>
              {(() => {
                const cheapest = rewards.filter(r => r.is_active !== false).reduce(
                  (m, r) => {
                    const pts = r.points_required || 0;
                    if (pts <= 0) return m;
                    return m === null || pts < m ? pts : m;
                  },
                  null,
                );
                return cheapest !== null
                  ? <>, y tu primera recompensa la tienes con solo <strong>{cheapest} puntos</strong></>
                  : null;
              })()}
              .{"\u201d"}
            </blockquote>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-card/70 p-3 border border-border/40">
                <div className="flex items-center gap-2 mb-1">
                  <Star className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium text-muted-foreground">Tasa de puntos</span>
                </div>
                <p className="text-lg font-semibold">{loyaltyConfig?.points_per_euro || 1} pt/€</p>
              </div>
              <div className="rounded-xl bg-card/70 p-3 border border-border/40">
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="w-4 h-4 text-fuchsia-500" />
                  <span className="text-xs font-medium text-muted-foreground">Recompensas activas</span>
                </div>
                <p className="text-lg font-semibold">{rewards.filter(r => r.is_active !== false).length}</p>
              </div>
              <div className="rounded-xl bg-card/70 p-3 border border-border/40">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs font-medium text-muted-foreground">Niveles</span>
                </div>
                <p className="text-lg font-semibold">{(loyaltyConfig?.levels || []).length || "—"}</p>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-none shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2"><QrCode className="w-5 h-5 text-primary" /> Portal de registro</CardTitle>
                <CardDescription>Comparte este QR para que los clientes se registren y vean su tarjeta.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {!qrData ? (
                  <div className="text-center py-8">
                    <QrCode className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-40" />
                    <p className="text-muted-foreground mb-4">Genera el código QR de registro</p>
                    <Button onClick={loadQR} disabled={qrLoading} data-testid="generate-qr-btn">
                      {qrLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
                      Generar QR
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      <div className="p-4 bg-card rounded-2xl shadow-inner border">
                        <img src={qrData.qr_code} alt="QR de fidelización" className="w-48 h-48" data-testid="loyalty-qr-image" />
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">URL del portal</p>
                      <div className="flex gap-2 items-center">
                        <code className="text-xs flex-1 truncate">{qrData.portal_url}</code>
                        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={copyUrl} data-testid="copy-portal-url-btn">
                          {copiedUrl ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" asChild>
                          <a href={qrData.portal_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3 h-3" /></a>
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1"
                        onClick={() => { const a = document.createElement("a"); a.href = qrData.qr_code; a.download = "qr-fidelizacion.png"; a.click(); }}
                        data-testid="download-qr-btn">
                        Descargar QR
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button className="flex-1" disabled={posterLoading} data-testid="download-pack-btn">
                            {posterLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}
                            Descargar pack visual
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-64">
                          <DropdownMenuItem onClick={() => handleDownloadAsset("a4")} data-testid="dl-a4">
                            <FileDown className="w-4 h-4 mr-2" />
                            <div className="flex flex-col">
                              <span className="font-medium">Póster A4 (PDF)</span>
                              <span className="text-xs text-muted-foreground">Imprime y cuelga en la pared</span>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadAsset("tent")} data-testid="dl-tent">
                            <FileDown className="w-4 h-4 mr-2" />
                            <div className="flex flex-col">
                              <span className="font-medium">Tarjeta de mesa 10×15 cm</span>
                              <span className="text-xs text-muted-foreground">PDF para porta-menús de mesa</span>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadAsset("story")} data-testid="dl-story">
                            <FileDown className="w-4 h-4 mr-2" />
                            <div className="flex flex-col">
                              <span className="font-medium">Imagen Stories (PNG)</span>
                              <span className="text-xs text-muted-foreground">1080×1920 para Instagram / WhatsApp</span>
                            </div>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button variant="ghost" size="icon" onClick={loadQR} disabled={qrLoading}><RefreshCw className="w-4 h-4" /></Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-none shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2"><Award className="w-5 h-5 text-primary" /> Cómo funciona</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { n: 1, t: "Escanear QR", d: "El cliente escanea el código QR y accede al portal." },
                  { n: 2, t: "Registrarse", d: "Se registra con Google o con un formulario (nombre, email, teléfono)." },
                  { n: 3, t: "Tarjeta digital", d: "Recibe una tarjeta con nombre, puntos y nivel de fidelización." },
                  { n: 4, t: "Acumular puntos", d: `Gana ${loyaltyConfig?.points_per_euro || 1} pt(s)/€. El restaurante registra cada consumo.` },
                  { n: 5, t: "Canjear recompensas", d: "Al llegar a los puntos necesarios puede canjear sus recompensas." }
                ].map(step => (
                  <div key={step.n} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">{step.n}</div>
                    <div><p className="font-medium text-sm">{step.t}</p><p className="text-xs text-muted-foreground">{step.d}</p></div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── DIALOGS ─────────────────────────────────────────────────────────── */}

      {/* Add customer */}
      <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo cliente</DialogTitle>
            <DialogDescription>Añade un cliente al programa de fidelización</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCustomer} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input placeholder="Nombre del cliente" value={customerForm.name} onChange={e => setCustomerForm({ ...customerForm, name: e.target.value })} data-testid="customer-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" placeholder="email@cliente.com" value={customerForm.email} onChange={e => setCustomerForm({ ...customerForm, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input placeholder="+34 600 000 000" value={customerForm.phone} onChange={e => setCustomerForm({ ...customerForm, phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fecha de cumpleaños</Label>
              <Input type="date" value={customerForm.birthday ? `2000-${customerForm.birthday}` : ""} onChange={e => {
                const val = e.target.value;
                if (val) {
                  const parts = val.split("-");
                  setCustomerForm({ ...customerForm, birthday: `${parts[1]}-${parts[2]}` });
                } else {
                  setCustomerForm({ ...customerForm, birthday: "" });
                }
              }} data-testid="customer-birthday-input" />
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea placeholder="Notas sobre el cliente..." value={customerForm.notes} onChange={e => setCustomerForm({ ...customerForm, notes: e.target.value })} rows={2} />
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setCustomerDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="save-customer-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Reward */}
      <Dialog open={rewardDialogOpen} onOpenChange={setRewardDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingReward ? "Editar recompensa" : "Nueva recompensa"}</DialogTitle>
            <DialogDescription>{editingReward ? "Modifica los datos de la recompensa" : "Define una nueva recompensa para el programa"}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveReward} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input placeholder="Ej: Café gratis, 10% descuento..." value={rewardForm.name}
                onChange={e => setRewardForm({ ...rewardForm, name: e.target.value })} data-testid="reward-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input placeholder="Descripción de la recompensa" value={rewardForm.description}
                onChange={e => setRewardForm({ ...rewardForm, description: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Tipo de recompensa</Label>
              <Select value={rewardForm.reward_type} onValueChange={val => setRewardForm({ ...rewardForm, reward_type: val })}>
                <SelectTrigger data-testid="reward-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="discount_percentage">% Descuento</SelectItem>
                  <SelectItem value="discount_amount">Descuento (€)</SelectItem>
                  <SelectItem value="free_item">Artículo gratis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Puntos necesarios *</Label>
                <Input type="number" placeholder="100" value={rewardForm.points_required}
                  onChange={e => setRewardForm({ ...rewardForm, points_required: e.target.value })}
                  data-testid="reward-points-input" />
              </div>
              <div className="space-y-2">
                <Label>{rewardForm.reward_type === "discount_percentage" ? "Valor (%)" : "Valor (€)"} *</Label>
                <Input type="number" step="0.01" placeholder={rewardForm.reward_type === "discount_percentage" ? "10" : "5"}
                  value={rewardForm.reward_value} onChange={e => setRewardForm({ ...rewardForm, reward_value: e.target.value })}
                  data-testid="reward-value-input" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={rewardForm.is_active} onCheckedChange={v => setRewardForm({ ...rewardForm, is_active: v })} data-testid="reward-active-switch" />
              <Label>Recompensa activa</Label>
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setRewardDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="save-reward-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingReward ? "Guardar cambios" : "Crear"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add purchase */}
      <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar consumo</DialogTitle>
            <DialogDescription>
              Añade puntos a {selectedCustomer?.name} según el importe.
              {loyaltyConfig && <span className="block text-xs mt-1">{loyaltyConfig.points_per_euro} pt(s)/€</span>}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddPurchase} className="space-y-4">
            <div className="space-y-2">
              <Label>Importe del consumo (€)</Label>
              <Input type="number" step="0.01" min="0.01" placeholder="25.50" value={purchaseAmount}
                onChange={e => setPurchaseAmount(e.target.value)} data-testid="purchase-amount-input" autoFocus />
              {purchaseAmount && loyaltyConfig && (
                <p className="text-sm text-primary font-medium">
                  +{Math.floor(parseFloat(purchaseAmount || 0) * (loyaltyConfig.points_per_euro || 1))} puntos
                </p>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setPurchaseDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="confirm-purchase-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Añadir puntos"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Redeem */}
      <Dialog open={redeemDialogOpen} onOpenChange={(open) => { setRedeemDialogOpen(open); if (!open) setCouponResult(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Canjear recompensa</DialogTitle>
            <DialogDescription>
              {selectedCustomer?.name} tiene <strong>{(selectedCustomer?.points || 0).toLocaleString()} puntos</strong>
            </DialogDescription>
          </DialogHeader>
          {couponResult ? (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="font-bold text-lg">{couponResult.reward_name}</p>
                <p className="text-muted-foreground">Código de descuento:</p>
                <code className="text-2xl font-bold text-primary tracking-wider">{couponResult.coupon_code}</code>
              </div>
              <p className="text-sm text-muted-foreground">Puntos usados: {couponResult.points_used} · Restantes: {couponResult.remaining_points}</p>
              <Button onClick={() => { setRedeemDialogOpen(false); setCouponResult(null); }}>Cerrar</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {rewards.filter(r => r.is_active).length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No hay recompensas activas. Crea o activa recompensas en "Programa".</p>
              ) : (
                rewards.filter(r => r.is_active).map(reward => {
                  const canRedeem = (selectedCustomer?.points || 0) >= reward.points_required;
                  return (
                    <div key={reward.reward_id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${canRedeem ? "border-primary/30 bg-primary/5" : "border-border opacity-60"}`}>
                      <div>
                        <p className="font-medium text-sm">{reward.name}</p>
                        <p className="text-xs text-muted-foreground">{reward.points_required} pts · {rewardLabel(reward.reward_type, reward.reward_value)}</p>
                      </div>
                      <Button size="sm" disabled={!canRedeem || saving} onClick={() => handleRedeemReward(reward.reward_id)}
                        data-testid={`redeem-reward-${reward.reward_id}`}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Canjear"}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Scanner dialog */}
      <LoyaltyScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        restaurantId={currentRestaurant?.restaurant_id}
        onSuccess={() => { fetchData(); }}
      />

      {/* CSV Import dialog (Fidelización Sprint 3) */}
      <CsvImportDialog
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        restaurantId={currentRestaurant?.restaurant_id}
        onImported={() => { fetchData(); }}
      />

      {/* Email Campaign dialog (Fidelización Sprint 3) */}
      <CampaignDialog
        open={campaignOpen}
        onClose={() => setCampaignOpen(false)}
        restaurantId={currentRestaurant?.restaurant_id}
        levels={configForm.levels || []}
        onSent={() => { fetchData(); }}
      />
    </div>
  );
}
